/**
 * Flow Layout Engine
 *
 * Calculates positions for nodes in the three-column Sankey diagram.
 * Based on the specification in docs/spec/sankey-visualization-engine.md
 *
 * WP-FLOW-6: Added asset/liability sorting within each tier
 */

import type {
  InflowNode,
  AccountNode,
  OutflowNode,
  LumpedNode,
  PositionedNode,
} from '../types';
import { isLiabilityAccount } from '../types';
import {
  CANVAS,
  COLUMNS,
  NODE,
  MARGIN,
  TIER,
  TIER_CONFIG,
} from '../constants';

/**
 * Calculate node height based on value relative to total
 * Ensures minimum height while keeping proportionality
 *
 * @param value - The node's value
 * @param totalValue - Total value of all nodes in the column
 * @param availableHeight - Available canvas height for nodes
 * @param minHeightFromEdges - Optional minimum height required to fit connected edges
 */
function calculateNodeHeight(
  value: number,
  totalValue: number,
  availableHeight: number,
  minHeightFromEdges?: number
): number {
  if (totalValue === 0) return Math.max(NODE.MIN_HEIGHT, minHeightFromEdges ?? 0);

  // Use 80% of available height for proportional sizing, leaving room for gaps
  const proportionalHeight = (value / totalValue) * availableHeight * 0.8;

  // Height must be at least: MIN_HEIGHT and minHeightFromEdges (if provided)
  const baseMin = Math.max(NODE.MIN_HEIGHT, minHeightFromEdges ?? 0);
  return Math.max(baseMin, proportionalHeight);
}

/**
 * Sort accounts within a tier: assets first (by flowTotal), then liabilities (by flowTotal)
 *
 * WP-FLOW-6: Within each tier, assets appear above liabilities.
 * Within each group, accounts are sorted by flowTotal (activity) descending.
 */
function sortAccountsWithinTier(accounts: AccountNode[]): AccountNode[] {
  const assets = accounts.filter((a) => !isLiabilityAccount(a.accountType));
  const liabilities = accounts.filter((a) => isLiabilityAccount(a.accountType));

  // Sort each group by flowTotal descending (most active at top)
  const sortByFlow = (a: AccountNode, b: AccountNode) =>
    (b.flowTotal ?? 0) - (a.flowTotal ?? 0);

  return [...assets.sort(sortByFlow), ...liabilities.sort(sortByFlow)];
}

/**
 * Layout inflow nodes (left column)
 * Sorts by value descending and positions vertically
 *
 * @param nodes - Inflow nodes to position
 * @param canvasHeight - Canvas height for layout calculations
 * @param minNodeHeights - Optional map of nodeId -> minimum height from connected edges
 */
export function layoutInflowNodes(
  nodes: InflowNode[],
  canvasHeight: number = CANVAS.HEIGHT,
  minNodeHeights?: Map<string, number>
): PositionedNode[] {
  if (nodes.length === 0) return [];

  // Sort by value descending
  const sortedNodes = [...nodes].sort((a, b) => b.value - a.value);

  // Calculate total value for proportional sizing
  const totalValue = sortedNodes.reduce((sum, node) => sum + node.value, 0);
  const availableHeight = canvasHeight - MARGIN.TOP - MARGIN.BOTTOM;

  const positioned: PositionedNode[] = [];
  let currentY = MARGIN.TOP;

  for (const node of sortedNodes) {
    // Get minimum height required from connected edges (if any)
    const minHeightFromEdges = minNodeHeights?.get(node.id);
    const height = calculateNodeHeight(node.value, totalValue, availableHeight, minHeightFromEdges);

    positioned.push({
      node,
      x: COLUMNS.INFLOW_X,
      y: currentY,
      width: NODE.WIDTH,
      height,
    });

    currentY += height + NODE.GAP;
  }

  return positioned;
}

/**
 * Create a lumped node from a group of accounts
 */
function createLumpedNode(
  tier: 'B' | 'C' | 'D',
  accounts: AccountNode[]
): LumpedNode {
  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);
  const aggregateInflow = accounts.reduce((sum, acc) => sum + Math.max(0, acc.flowTotal), 0);
  const aggregateOutflow = accounts.reduce((sum, acc) => sum + Math.abs(Math.min(0, acc.flowTotal)), 0);

  return {
    id: `tier-${tier}-lumped`,
    type: 'lumped',
    name: TIER_CONFIG[tier].label,
    totalBalance,
    aggregateInflow,
    aggregateOutflow,
    tier,
    containedAccounts: accounts,
  };
}

/**
 * Layout account nodes (middle column)
 * Groups by tier with optional expansion
 *
 * WP-FLOW-6: Sorts accounts within each tier with assets above liabilities.
 * Within each asset/liability group, sorts by flowTotal (activity) descending.
 *
 * @param accounts - Account nodes to position
 * @param expandedTiers - Array of tier IDs that should show individual accounts
 * @param _canvasHeight - Canvas height (unused but kept for API consistency)
 * @param minNodeHeights - Optional map of nodeId -> minimum height from connected edges
 * @param preLumpedNodes - Optional pre-lumped nodes from backend (used when tiers are not expanded)
 */
export function layoutAccountNodes(
  accounts: AccountNode[],
  expandedTiers: string[] = [],
  _canvasHeight: number = CANVAS.HEIGHT,
  minNodeHeights?: Map<string, number>,
  preLumpedNodes: LumpedNode[] = []
): PositionedNode[] {
  // Build a map of pre-lumped nodes by tier for quick lookup
  const preLumpedByTier = new Map<string, LumpedNode>();
  for (const node of preLumpedNodes) {
    preLumpedByTier.set(node.tier, node);
  }

  // If no accounts AND no pre-lumped nodes, return empty
  if (accounts.length === 0 && preLumpedNodes.length === 0) return [];

  // Group accounts by tier
  const tierAUnsorted = accounts.filter((acc) => acc.tier === 'A');
  const tierBUnsorted = accounts.filter((acc) => acc.tier === 'B');
  const tierCUnsorted = accounts.filter((acc) => acc.tier === 'C');
  const tierDUnsorted = accounts.filter((acc) => acc.tier === 'D');

  // Apply asset/liability ordering within each tier (WP-FLOW-6)
  const tierA = sortAccountsWithinTier(tierAUnsorted);
  const tierB = sortAccountsWithinTier(tierBUnsorted);
  const tierC = sortAccountsWithinTier(tierCUnsorted);
  const tierD = sortAccountsWithinTier(tierDUnsorted);

  const positioned: PositionedNode[] = [];
  let currentY = MARGIN.TOP;

  // Calculate center X position for account column
  const accountX = COLUMNS.ACCOUNT_X;

  // Pre-fetch lumped nodes for tier B, C, and D to use in divider logic
  const preLumpedB = preLumpedByTier.get('B');
  const preLumpedC = preLumpedByTier.get('C');
  const preLumpedD = preLumpedByTier.get('D');
  const hasTierB = tierB.length > 0 || preLumpedB;
  const hasTierC = tierC.length > 0 || preLumpedC;
  const hasTierD = tierD.length > 0 || preLumpedD;

  // Tier A: Always show individual accounts
  for (const account of tierA) {
    // Use minimum height from edges if it exceeds default ACCOUNT_HEIGHT
    const minHeightFromEdges = minNodeHeights?.get(account.id) ?? 0;
    const height = Math.max(NODE.ACCOUNT_HEIGHT, minHeightFromEdges);

    positioned.push({
      node: account,
      x: accountX,
      y: currentY,
      width: NODE.ACCOUNT_WIDTH,
      height,
    });
    currentY += height + NODE.GAP;
  }

  // Add tier divider space if there are tier B, C, or D accounts (or pre-lumped nodes)
  if (hasTierB || hasTierC || hasTierD) {
    currentY += TIER.DIVIDER_HEIGHT;
  }

  // Tier B: Lumped or expanded

  if (hasTierB) {
    if (expandedTiers.includes('B')) {
      // Expanded: show individual accounts (already sorted)
      for (const account of tierB) {
        const minHeightFromEdges = minNodeHeights?.get(account.id) ?? 0;
        const height = Math.max(NODE.ACCOUNT_HEIGHT, minHeightFromEdges);

        positioned.push({
          node: account,
          x: accountX,
          y: currentY,
          width: NODE.ACCOUNT_WIDTH,
          height,
        });
        currentY += height + NODE.GAP;
      }
    } else {
      // Lumped: use pre-lumped node from backend if available, otherwise create one
      const lumpedNode = preLumpedB ?? createLumpedNode('B', tierB);
      // For lumped nodes, check minimum height using the lumped node's ID
      const minHeightFromEdges = minNodeHeights?.get(lumpedNode.id) ?? 0;
      const height = Math.max(NODE.LUMPED_HEIGHT, minHeightFromEdges);

      positioned.push({
        node: lumpedNode,
        x: accountX,
        y: currentY,
        width: NODE.ACCOUNT_WIDTH,
        height,
      });
      currentY += height + NODE.GAP;
    }

    // Add divider between B and C/D if C or D exists
    if (hasTierC || hasTierD) {
      currentY += TIER.DIVIDER_HEIGHT;
    }
  }

  // Tier C: Lumped or expanded
  if (hasTierC) {
    if (expandedTiers.includes('C')) {
      // Expanded: show individual accounts (already sorted)
      for (const account of tierC) {
        const minHeightFromEdges = minNodeHeights?.get(account.id) ?? 0;
        const height = Math.max(NODE.ACCOUNT_HEIGHT, minHeightFromEdges);

        positioned.push({
          node: account,
          x: accountX,
          y: currentY,
          width: NODE.ACCOUNT_WIDTH,
          height,
        });
        currentY += height + NODE.GAP;
      }
    } else {
      // Lumped: use pre-lumped node from backend if available, otherwise create one
      const lumpedNode = preLumpedC ?? createLumpedNode('C', tierC);
      const minHeightFromEdges = minNodeHeights?.get(lumpedNode.id) ?? 0;
      const height = Math.max(NODE.LUMPED_HEIGHT, minHeightFromEdges);

      positioned.push({
        node: lumpedNode,
        x: accountX,
        y: currentY,
        width: NODE.ACCOUNT_WIDTH,
        height,
      });
      currentY += height + NODE.GAP;
    }

    // Add divider between C and D if D exists
    if (hasTierD) {
      currentY += TIER.DIVIDER_HEIGHT;
    }
  }

  // Tier D: Lumped or expanded (Other Liabilities)
  if (hasTierD) {
    if (expandedTiers.includes('D')) {
      // Expanded: show individual accounts (already sorted)
      for (const account of tierD) {
        const minHeightFromEdges = minNodeHeights?.get(account.id) ?? 0;
        const height = Math.max(NODE.ACCOUNT_HEIGHT, minHeightFromEdges);

        positioned.push({
          node: account,
          x: accountX,
          y: currentY,
          width: NODE.ACCOUNT_WIDTH,
          height,
        });
        currentY += height + NODE.GAP;
      }
    } else {
      // Lumped: use pre-lumped node from backend if available, otherwise create one
      const lumpedNode = preLumpedD ?? createLumpedNode('D', tierD);
      const minHeightFromEdges = minNodeHeights?.get(lumpedNode.id) ?? 0;
      const height = Math.max(NODE.LUMPED_HEIGHT, minHeightFromEdges);

      positioned.push({
        node: lumpedNode,
        x: accountX,
        y: currentY,
        width: NODE.ACCOUNT_WIDTH,
        height,
      });
      currentY += height + NODE.GAP;
    }
  }

  return positioned;
}

/**
 * Layout outflow nodes (right column)
 * Same algorithm as inflow but on right side
 *
 * @param nodes - Outflow nodes to position
 * @param canvasHeight - Canvas height for layout calculations
 * @param minNodeHeights - Optional map of nodeId -> minimum height from connected edges
 */
export function layoutOutflowNodes(
  nodes: OutflowNode[],
  canvasHeight: number = CANVAS.HEIGHT,
  minNodeHeights?: Map<string, number>
): PositionedNode[] {
  if (nodes.length === 0) return [];

  // Sort by value descending
  const sortedNodes = [...nodes].sort((a, b) => b.value - a.value);

  // Calculate total value for proportional sizing
  const totalValue = sortedNodes.reduce((sum, node) => sum + node.value, 0);
  const availableHeight = canvasHeight - MARGIN.TOP - MARGIN.BOTTOM;

  const positioned: PositionedNode[] = [];
  let currentY = MARGIN.TOP;

  for (const node of sortedNodes) {
    // Get minimum height required from connected edges (if any)
    const minHeightFromEdges = minNodeHeights?.get(node.id);
    const height = calculateNodeHeight(node.value, totalValue, availableHeight, minHeightFromEdges);

    positioned.push({
      node,
      x: COLUMNS.OUTFLOW_X,
      y: currentY,
      width: NODE.WIDTH,
      height,
    });

    currentY += height + NODE.GAP;
  }

  return positioned;
}

/**
 * Find a positioned node by ID
 */
export function findPositionedNode(
  nodeId: string,
  allPositioned: PositionedNode[]
): PositionedNode | undefined {
  // Direct match
  const direct = allPositioned.find((p) => p.node.id === nodeId);
  if (direct) return direct;

  // Check if the node is contained in a lumped node
  for (const positioned of allPositioned) {
    if (positioned.node.type === 'lumped') {
      const lumpedNode = positioned.node as LumpedNode;
      const contained = lumpedNode.containedAccounts.find(
        (acc) => acc.id === nodeId
      );
      if (contained) {
        // Return the lumped node position (edges will point to lumped node)
        return positioned;
      }
    }
  }

  return undefined;
}

/**
 * Get the center point of a positioned node
 */
export function getNodeCenter(positioned: PositionedNode): { x: number; y: number } {
  return {
    x: positioned.x + positioned.width / 2,
    y: positioned.y + positioned.height / 2,
  };
}

/**
 * Get the right edge center point (for connecting outgoing edges)
 */
export function getNodeRightCenter(positioned: PositionedNode): { x: number; y: number } {
  return {
    x: positioned.x + positioned.width,
    y: positioned.y + positioned.height / 2,
  };
}

/**
 * Get the left edge center point (for connecting incoming edges)
 */
export function getNodeLeftCenter(positioned: PositionedNode): { x: number; y: number } {
  return {
    x: positioned.x,
    y: positioned.y + positioned.height / 2,
  };
}

/**
 * Calculate the required SVG height based on actual node positions
 * Returns at least the minimum canvas height, but expands if nodes extend beyond
 */
export function calculateRequiredHeight(
  inflowNodes: PositionedNode[],
  accountNodes: PositionedNode[],
  outflowNodes: PositionedNode[]
): number {
  const allNodes = [...inflowNodes, ...accountNodes, ...outflowNodes];

  if (allNodes.length === 0) {
    return CANVAS.HEIGHT;
  }

  // Find the maximum Y position (bottom of lowest node)
  const maxY = Math.max(...allNodes.map((n) => n.y + n.height));

  // Add bottom margin for padding
  const requiredHeight = maxY + MARGIN.BOTTOM;

  // Return at least the minimum height, but expand if nodes extend beyond
  return Math.max(CANVAS.HEIGHT, requiredHeight);
}

// Export the sorting function for testing
export { sortAccountsWithinTier };
